import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { DocumentItem } from '../../interfaces/document-item.interface';
import {
  DocumentPreviewDocument,
  DocumentSplitDialogData,
  DocumentSplitReason,
  LineAltUnits,
} from '../../interfaces/dialog-data.interface';
import { DgNumberPipe } from '../../dg-number.pipe';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslationKey } from 'app/core/i18n/translation-keys';

// clave de traducción de cada motivo de separación; as const conserva el literal,
// sin él la clave se ensancha y t() deja de saber que no lleva parámetros
const SPLIT_REASON_KEYS = {
  WAREHOUSE: 'DOCUMENT_GENERATE.SPLIT_MODAL.REASON_WAREHOUSE',
  WITHOUT_STOCK: 'DOCUMENT_GENERATE.SPLIT_MODAL.REASON_WITHOUT_STOCK',
  DELIVERY_WINDOW: 'DOCUMENT_GENERATE.SPLIT_MODAL.REASON_DELIVERY_WINDOW',
} as const satisfies Record<DocumentSplitReason, TranslationKey>;

// Documentos que producirá la partición, en lista de selección y detalle
@Component({
  selector: 'app-document-split-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    DgNumberPipe,
    TranslatePipe,
  ],
  templateUrl: './document-split-modal.component.html',
})
export class DocumentSplitModalComponent implements OnInit, OnDestroy {
  selectedIndex = 0;

  // en pantallas de poca altura (móvil horizontal) el encabezado no deja ver el detalle
  shortScreen = false;
  private shortScreenSub?: Subscription;

  constructor(
    private dialogRef: MatDialogRef<DocumentSplitModalComponent>,
    private breakpointObserver: BreakpointObserver,
    private i18n: LanguageService,
    @Inject(MAT_DIALOG_DATA) public data: DocumentSplitDialogData
  ) {}

  ngOnInit(): void {
    this.shortScreenSub = this.breakpointObserver
      .observe('(max-height: 560px)')
      .subscribe((state) => (this.shortScreen = state.matches));
  }

  ngOnDestroy(): void {
    this.shortScreenSub?.unsubscribe();
  }

  get documents(): DocumentPreviewDocument[] {
    return this.data.documents ?? [];
  }

  get selected(): DocumentPreviewDocument | null {
    return this.documents[this.selectedIndex] ?? null;
  }

  select(index: number): void {
    this.selectedIndex = index;
  }

  lineCount(document: DocumentPreviewDocument): number {
    return document.items?.length ?? 0;
  }

  warehouseLabel(item: DocumentItem): string {
    const code = item.warehouseCode ?? '';
    const name = item.warehouseName ?? '';
    return code && name ? `${code} - ${name}` : code || name;
  }

  altUnits(item: DocumentItem): LineAltUnits {
    return this.selected?.altUnits?.[item.lineId] ?? {};
  }

  readonly columnCount = 5;

  // la ruta se resolvió para este documento, tenga o no candidata elegible
  hasRouteResolution(document: DocumentPreviewDocument | null): boolean {
    return !!document?.routeGroup;
  }

  withoutRoute(document: DocumentPreviewDocument): boolean {
    return !!document.routeGroup && !document.routeName;
  }

  // abierto desde el modal de rutas no se ofrece cambiarla: se elige allá mismo
  canChangeRoute(document: DocumentPreviewDocument | null): boolean {
    return !!this.data.onChangeRoute && (document?.routeGroup?.candidates?.length ?? 0) > 1;
  }

  changeRoute(): void {
    const document = this.selected;
    if (!document || !this.canChangeRoute(document)) return;
    this.data.onChangeRoute?.(document);
  }

  // motivo de separación, en el idioma activo
  reasonLabel(reason: DocumentSplitReason): string {
    return this.i18n.t(SPLIT_REASON_KEYS[reason]);
  }

  // la cantidad se muestra con los decimales que admite la unidad de la línea
  quantityText(item: DocumentItem): string {
    const decimals = item.hasAcceptedDecimals ? Number(item.decimalsAllowed ?? 0) || 0 : 0;
    const text = (Number(item.quantity) || 0).toFixed(decimals);
    return text.includes('.') ? text.replace(/\.?0+$/, '') : text;
  }

  rowSubtotal(item: DocumentItem): number {
    return item.quantity * item.price - (this.data.taxIncluded ? item.isv : 0);
  }

  rowTotal(item: DocumentItem): number {
    return this.rowSubtotal(item) - item.discount + item.isv;
  }

  close(): void {
    this.dialogRef.close();
  }
}
